import ConfirmModal from "@/components/molecules/modals/ats/confirm-modal";
import { FiChevronLeft } from "react-icons/fi";
import Buttons from "../../../../../components/button";
import { ControlTabs } from "../../../../../components/control-tabs/control-tabs";
import { useFieldSet } from "../../../../../components/utils/useFieldSet";
import {
  JobAppDocumentUploadRequest,
  JobAppPersonalInformationRequest,
  JobAppRequestCreationType,
  JobAppSectionResponseSectionType,
  useFetchOrgIsGovernmentContractor,
  useFetchOrgLogoLinkv2,
  useGetCandidatePublic,
  useGetCareerPageJobDetails,
  useUploadCandidateJobAppDocument,
  useUpsertCandidateJobApp,
} from "../../../../api-docs";
import { Header as PageHeader } from "../../../components/header/header";
import { CandidateJobDetailsPane } from "./components/candidate-job-details-pane";

import { URLS } from "../../../../../utils/urls";
import { StickySidebarPageLayout } from "../../../layouts/sticky-sidebar-layout";

import React, { useEffect, useMemo, useState } from "react";
import { HStack } from "../../../../../components/composition/stack.layout";
import {
  GenericLoadingBounce,
  GenericLoadingOverlay,
} from "../../../../../components/generic-loading";
import { When } from "../../../../../components/when";

import { doNothing } from "@/base/utils/functional";
import { ModalService } from "@/components/services/modal.service";
import { Button } from "@/flokit/core/button/button";
import { showToast } from "@/flokit/core/toast/show-toast";
import { NonNullish } from "@/flokit/utils/comparison/non-nullish";
import { cn } from "@/flokit/utils/styling/cn";
import { toValidLuxonDateTime } from "@/utils/dates/to-luxon-datetime";
import { isCurrentDateAfterStartDate } from "@/utils/datetime-utils";
import { useServerTimeDeadline } from "@/utils/useServerTime";
import { isNil } from "lodash";
import { ClientCandidateSelfApplyJobAppRequest } from "modules/self-apply/candidate/utils/handle-submit";
import {
  CandidateSelfApplyJobApplicationSchema,
  CandidateSelfApplyPersonalInfoSchema,
} from "modules/self-apply/validation/candidate-self-apply";
import { validateAllFieldSets } from "modules/work-allocation/utils/validate-all-field-sets";
import { useRouter } from "next/router";
import { useLeaveConfirmation } from "../../../../../components/confirm-leave-guard";
import { openApplicantSignInModal } from "../../../../../components/molecules/modals/ats/public-sign-in-modal.tsx/verify-email-sign-in-modal";
import { ScrollIntoView } from "../../../../../components/scroll-into-view";
import { PanelService } from "../../../../../components/services/panel.service";
import useGetApplicantLoginInfo from "../../../../../components/utils/useGetApplicantLoginInfo";
import {
  PublicJobAppQuery,
  useCandidateOrgJobAppOverviewsQuery,
  usePublicJobAppQuery,
  usePublicJobQuery,
  usePublicOrgSelfIdQuestionsQuery,
} from "../../../../graphql.types";
import { SelfApplyApplicationPageView } from "../../../common/pages/self-apply-application";
import { SelfApplyAddressInfoAppSection } from "../../../common/pages/self-apply-application/job-app-sections/address-info";
import { SelfApplyCandidateDocumentsAppSection } from "../../../common/pages/self-apply-application/job-app-sections/candidate-documents";
import { SelfApplyCustomQuestionsAppSection } from "../../../common/pages/self-apply-application/job-app-sections/custom-questions";
import { SelfApplyEducationHistoryInfoAppSection } from "../../../common/pages/self-apply-application/job-app-sections/education-history";
import { SelfApplyPersonalInfoAppSection } from "../../../common/pages/self-apply-application/job-app-sections/personal-info";
import { SelfApplySelfIDAppSection } from "../../../common/pages/self-apply-application/job-app-sections/self-identification";
import { SelfApplyWorkHistoryAppSection } from "../../../common/pages/self-apply-application/job-app-sections/work-history";
import { EmailSignInButton } from "../../../components/email-sign-in-button";
import { HeaderLogo } from "../../../components/header-logo";
import {
  APP_STATUS,
  AppStatus,
} from "../../../job-list/components/app-status-badge";
import { useAddressInfoFieldSet } from "./logic/address-info";
import {
  bundleFieldSetsToFormRequest,
  uploadSelfApplyJobApplication,
  UploadSelfApplyJobApplicationOnError,
  validateSelfApplyJobApplication,
  ValidateSelfApplyJobApplicationOnError,
} from "./logic/all-together";
import { useCustomQuestionAnswersFieldSet } from "./logic/custom-questions";
import {
  documentFieldsToRequests,
  useDocumentFieldSet,
} from "./logic/documents";
import { useEducationHistoryFieldSet } from "./logic/education-history";
import { useSelfIdQuestionAnswersFieldSet } from "./logic/self-id";
import { useWorkHistoryFieldSet } from "./logic/work-history";
import { ForwardJobApplicationSource } from "@/modules/forward/pendo-features";

type JobApplicationPageProps = {
  jobExtension: string;
  orgFriendlyName: string;
};
export const JobApplicationPage = (props: JobApplicationPageProps) => {
  const router = useRouter();
  const { applicantLoginInfo } = useGetApplicantLoginInfo();
  const isLoggedIn = !!applicantLoginInfo?.success;

  // TODO: Pass down as props?
  // Pre-load candidate's info so that when they go to the tab it is already fetched
  useGetCandidatePublic(props.orgFriendlyName, {
    query: { enabled: isLoggedIn },
  });

  // For header
  const logoRes = useFetchOrgLogoLinkv2(
    { orgFriendlyName: props.orgFriendlyName },
    {
      query: {
        enabled: props.orgFriendlyName != null,
      },
    },
  );

  const [tab, setTab] =
    useState<SelfApplyApplicationPageView>("job-description");

  // Move candidate out of job app tab if they're not logged in
  useEffect(() => {
    if (tab === "application-form") {
      if (!isLoggedIn) {
        setTab("job-description");
        openApplicantSignInModal();
      }
    }
  }, [isLoggedIn, tab]);

  // Depending on what stage of filling out the form the candidate is in, warn them when they're about to leave the page
  const [leaveConfirm, setLeaveConfirm] = useState(true);
  useLeaveConfirmation({
    message: "Are you sure you want to leave this page?",
    destinationRoute: URLS.selfApplyJobsList(props.orgFriendlyName),
    enabled: leaveConfirm && tab === "application-form",
  });

  const jobDetailResponse = useGetCareerPageJobDetails(
    props.orgFriendlyName,
    props.jobExtension,
    {
      query: {
        enabled: !!props.orgFriendlyName,
      },
    },
  );

  const { mutateAsync: upsertForm, isLoading: isLoadingUpsertForm } =
    useUpsertCandidateJobApp();
  const { mutateAsync: uploadDocument, isLoading: isLoadingUploadDocument } =
    useUploadCandidateJobAppDocument();
  const isSubmitting = isLoadingUpsertForm || isLoadingUploadDocument;

  // Determine whether candidate has already applied to this job
  //

  const jobAppsRequest = useCandidateOrgJobAppOverviewsQuery(
    {
      orgFriendlyName: props.orgFriendlyName,
    },
    {
      enabled: isLoggedIn && !!props.orgFriendlyName,
    },
  );

  // TODO: Why can't we just determine this from usePublicJobAppQuery?
  const appIsExisting = useMemo(() => {
    return (
      jobAppsRequest?.data?.publicCandidateOrgJobApps?.some(
        (a) => a?.jobId === jobDetailResponse?.data?.id,
      ) ?? false
    );
  }, [jobAppsRequest.data, jobDetailResponse.data]);

  //

  const jobAppRequest = usePublicJobAppQuery(
    {
      orgFriendlyName: props.orgFriendlyName,
      jobExtension: props.jobExtension,
    },
    {
      enabled: !!props.orgFriendlyName,
    },
  );

  const existingJobApp: PublicJobAppQuery["publicCandidateJobApp"] =
    jobAppRequest?.data?.publicCandidateJobApp ?? {};

  const closeDateDeadline = useServerTimeDeadline({
    until: toValidLuxonDateTime(jobDetailResponse.data?.closeDate ?? null),
    enabled: !!jobDetailResponse.data?.closeDate,
  });

  function isApplicationTabEnabled() {
    const isJobOpen = isCurrentDateAfterStartDate(
      jobDetailResponse?.data?.openDate,
    );
    if (!isJobOpen) {
      return false;
    }

    if (existingJobApp?.missingInfoRequestToCandidate) {
      return true;
    }

    return !(
      closeDateDeadline === "after" ||
      (existingJobApp?.isComplete &&
        existingJobApp?.creationType !== JobAppRequestCreationType.MANUAL)
    );
  }

  // Get schema for job app to do loading state stuff
  const jobSchema = usePublicJobQuery(
    {
      jobExtension: props.jobExtension,
      orgFriendlyName: props.orgFriendlyName,
    },
    {
      enabled: isLoggedIn && !!props.orgFriendlyName,
      refetchOnWindowFocus: false,
    },
  );

  const isGovtContractorResponse = useFetchOrgIsGovernmentContractor(
    {
      orgFriendlyName: props.orgFriendlyName,
    },
    {
      query: {
        enabled: isLoggedIn && !!props.orgFriendlyName,
      },
    },
  );

  function isLoading() {
    let isLoading =
      (tab === "application-form" && !jobSchema.isFetched) ||
      !jobDetailResponse.isFetched;

    // if not logged in, we don't need to wait for the govt contractor response since it's not enabled
    if (isLoggedIn) {
      isLoading ||= !isGovtContractorResponse.isFetched;
    }

    return isLoading;
  }

  if (isLoading()) {
    return (
      <StickySidebarPageLayout.Container>
        <StickySidebarPageLayout.Header>
          <PageHeader />
        </StickySidebarPageLayout.Header>
        <StickySidebarPageLayout.Content>
          <HStack className="w-full items-center justify-center">
            <GenericLoadingBounce message="Loading..." />
          </HStack>
        </StickySidebarPageLayout.Content>
      </StickySidebarPageLayout.Container>
    );
  }

  // greg note: idk what is going on here
  if (
    (tab === "application-form" && !jobSchema.isFetched) ||
    !isGovtContractorResponse.isFetched
  ) {
    if (!jobDetailResponse.data) {
      return null;
    }
  }

  // Text to show in tab on left side of page
  function getAppStatus(): AppStatus {
    // for missing data requests, candidate should be able to see application even if the job close date has passed
    if (existingJobApp?.missingInfoRequestToCandidate) {
      return "MISSING_INFO_REQUESTED";
    }
    if (closeDateDeadline == "after") {
      return "DEADLINE_PASSED";
    }
    if (existingJobApp?.creationType === JobAppRequestCreationType.MANUAL) {
      return "INFO_PENDING";
    }
    if (
      existingJobApp?.isComplete &&
      existingJobApp?.creationType !== JobAppRequestCreationType.MANUAL
    ) {
      return "APPLIED";
    }
    return "UNDEFINED";
  }

  function isSubmitDisabled() {
    if (existingJobApp?.missingInfoRequestToCandidate) {
      return false;
    }

    return (
      existingJobApp?.isComplete &&
      existingJobApp?.creationType !== JobAppRequestCreationType.MANUAL
    );
  }

  return (
    <React.Fragment>
      <When condition={isSubmitting}>
        <GenericLoadingOverlay message={"Submitting your application."} />
      </When>
      <StickySidebarPageLayout.Container desktopSidebarContainerClass="w-full max-w-[228px]">
        <StickySidebarPageLayout.Header>
          <PageHeader
            leading={
              <HeaderLogo
                logo={logoRes?.data ?? ""}
                sidebar={
                  <div className="flex h-full w-full flex-col gap-y-4 bg-white px-4 py-4 shadow">
                    <Buttons.LinkStandard
                      href={URLS.selfApplyJobsList(props.orgFriendlyName)}
                      onClick={() => PanelService.close()}
                      className="flex items-start gap-x-2"
                      variant="secondary"
                    >
                      <FiChevronLeft size={18} />
                      <span className="font-16">Back to Job Listings</span>
                    </Buttons.LinkStandard>
                    <Buttons.Standard
                      onClick={() => {
                        setTab("job-description");
                        PanelService.close();
                      }}
                      className="flex items-start gap-x-2"
                      variant="primary-navy"
                    >
                      Job Overview
                    </Buttons.Standard>
                    <Buttons.Standard
                      disabled={appIsExisting}
                      onClick={() => {
                        if (appIsExisting) {
                          return;
                        }
                        setTab("application-form");
                        PanelService.close();
                      }}
                      className="flex items-start gap-x-2"
                      variant="tertiary"
                    >
                      Application
                    </Buttons.Standard>
                  </div>
                }
              />
            }
            trailing={
              <div className="flex w-full items-center justify-end">
                <EmailSignInButton
                  isLoggedIn={
                    applicantLoginInfo?.success && applicantLoginInfo.email
                      ? { email: applicantLoginInfo.email }
                      : undefined
                  }
                  onClick={() => openApplicantSignInModal()}
                />
              </div>
            }
          />
        </StickySidebarPageLayout.Header>
        <StickySidebarPageLayout.DesktopSidebar>
          <div className="flex w-full flex-col gap-y-4 py-10">
            <Buttons.LinkStandard
              variant="secondary"
              href={URLS.selfApplyJobsList(props.orgFriendlyName)}
              className="flex w-full items-center gap-x-2"
            >
              <FiChevronLeft size={18} />
              <span className="font-16">Back to Job Listings</span>
            </Buttons.LinkStandard>
            <ControlTabs<SelfApplyApplicationPageView>
              items={[
                {
                  label: "Job Overview",
                  value: "job-description",
                  render: (
                    <HStack key={"job_overview_tab"}>
                      <span>Job Overview</span>
                    </HStack>
                  ),
                },
                {
                  label: "Application",
                  value: "application-form",
                  render: (
                    <div
                      key={"application_tab"}
                      className="flex flex-col flex-wrap items-start gap-y-2"
                    >
                      <HStack className="pr-2">Application</HStack>
                      {APP_STATUS[getAppStatus()]()}
                    </div>
                  ),
                  isDisabled: !isApplicationTabEnabled(),
                },
              ]}
              selected={tab}
              setSelected={(value) => {
                if (!isApplicationTabEnabled()) {
                  return;
                }
                setTab(value);
              }}
            />
          </div>
        </StickySidebarPageLayout.DesktopSidebar>
        <StickySidebarPageLayout.Content>
          <div className="flex w-full flex-col gap-y-6 pb-10 pt-4">
            <When condition={tab === "job-description"}>
              <CandidateJobDetailsPane
                org={props.orgFriendlyName}
                jobId={props.jobExtension}
                jobExtension={props.jobExtension}
                onRequestApplication={() => setTab("application-form")}
                allowRequestApplication
                isUpdate={!!existingJobApp.missingInfoRequestToCandidate}
              />
            </When>
            <div
              className={cn(
                // Extra markup don't affect CSS
                "contents",
                // Using this rather than <When> so that changing tabs doesn't throw away the form state
                tab === "application-form" ? "block" : "hidden",
              )}
            >
              <ScrollIntoView />
              <ConnectedSelfApplyForm
                orgFriendlyName={props.orgFriendlyName}
                jobExtension={props.jobExtension}
                submission={{
                  isDisabled: !!isSubmitDisabled(),
                  isLoading: isSubmitting,
                  onSubmit: () => {
                    setLeaveConfirm(false);
                  },
                  onValidate: {
                    onSuccess: doNothing,
                    onError: () => {
                      setLeaveConfirm(true);
                      showToast({
                        type: "error",
                        message:
                          "There were some errors with your application, please make sure all required fields are filled out.",
                      });
                    },
                  },
                  onUpload: {
                    uploadForm: async (formRequest) => {
                      return upsertForm({ data: formRequest });
                    },
                    uploadDocument: async (documentRequest) => {
                      await uploadDocument({ data: documentRequest });
                    },
                    onSuccess: () => {
                      router.push(
                        URLS.selfApplyJobApplicationSuccess(
                          props.orgFriendlyName,
                          props.jobExtension,
                        ),
                      );
                    },
                    onError: () => {
                      setLeaveConfirm(true);
                    },
                  },
                }}
              />
            </div>
          </div>
        </StickySidebarPageLayout.Content>
      </StickySidebarPageLayout.Container>
    </React.Fragment>
  );
};

const EMPTY_OBJECT = {};

type ConnectionSelfApplyFormProps = {
  jobExtension: string;
  orgFriendlyName: string;
  /**
   * Handlers for submission
   */
  submission: {
    isDisabled: boolean;
    isLoading: boolean;
    onSubmit?: () => void;
    /**
     * When validating the form. Occurs before uploading.
     */
    onValidate: {
      onSuccess?: () => void;
      onError?: ValidateSelfApplyJobApplicationOnError;
      onSettled?: () => void;
    };
    /**
     * When uploading the job application form fields, followed by any documents. Occurs after validating.
     */
    onUpload: {
      uploadForm: (
        formRequest: ClientCandidateSelfApplyJobAppRequest,
      ) => Promise<number>;
      uploadDocument: (
        doumentRequest: JobAppDocumentUploadRequest,
      ) => Promise<void>;
      onSuccess?: () => void;
      onError?: UploadSelfApplyJobApplicationOnError;
      onSettled?: () => void;
    };
  };
  submitButtonGoToBottomOfContainer?: boolean;
  submittedJobOnForward?: boolean;
  forwardJobData?: {
    orgId: string;
    jobId: string;
    source: ForwardJobApplicationSource;
  };
};
export const ConnectedSelfApplyForm = (props: ConnectionSelfApplyFormProps) => {
  const { applicantLoginInfo } = useGetApplicantLoginInfo();
  const isLoggedIn = !!applicantLoginInfo?.success;
  const userEmail = applicantLoginInfo?.email ?? "";

  // Form structure
  //

  const jobSchema = usePublicJobQuery(
    {
      jobExtension: props.jobExtension,
      orgFriendlyName: props.orgFriendlyName,
    },
    {
      enabled: isLoggedIn && !!props.orgFriendlyName,
      refetchOnWindowFocus: false,
    },
  );

  const schema = jobSchema?.data?.publicJob ?? null;

  const jobSections = useMemo(() => {
    return schema?.jobSections?.filter(NonNullish) ?? [];
  }, [schema]);

  //

  // Default values
  //

  const jobAppRequest = usePublicJobAppQuery(
    {
      orgFriendlyName: props.orgFriendlyName,
      jobExtension: props.jobExtension,
    },
    {
      enabled: !!props.orgFriendlyName,
    },
  );

  const existingJobApp: PublicJobAppQuery["publicCandidateJobApp"] =
    jobAppRequest?.data?.publicCandidateJobApp ?? EMPTY_OBJECT;

  //

  // Personal info populated from logged-in user
  //

  // TODO: Pull up to props; in self apply we want to populate from RECRUITS record, which may not exist yet, so it makes sense for it to be empty. In quick apply (Forward) we should always have the necessary info to fill this in
  const candidateResponse = useGetCandidatePublic(props.orgFriendlyName, {
    query: { enabled: isLoggedIn },
  });

  const firstName = candidateResponse?.data?.firstName ?? "";
  const lastName = candidateResponse?.data?.lastName ?? "";
  const phoneNumber = candidateResponse?.data?.phoneNumber ?? "";
  const linkedIn = candidateResponse?.data?.linkedIn ?? "";

  const personalInfoFieldSet = useFieldSet<JobAppPersonalInformationRequest>(
    {
      firstName: firstName,
      lastName: lastName,
      email: userEmail,
      phoneNumber: phoneNumber,
      linkedIn: linkedIn,
    },
    [userEmail, firstName, lastName, phoneNumber, linkedIn],
    CandidateSelfApplyPersonalInfoSchema,
  );

  //

  // Self id section
  //

  const selfIdQuestionResponses = usePublicOrgSelfIdQuestionsQuery(
    {
      orgFriendlyName: props.orgFriendlyName,
    },
    { enabled: !!props.orgFriendlyName },
  );

  const isGovtContractorResponse = useFetchOrgIsGovernmentContractor(
    {
      orgFriendlyName: props.orgFriendlyName,
    },
    {
      query: {
        enabled: isLoggedIn && !!props.orgFriendlyName,
      },
    },
  );

  const selfIdQuestions = useMemo(() => {
    return (
      selfIdQuestionResponses?.data?.publicOrgSelfIdQuestions?.filter(
        NonNullish,
      ) ?? []
    );
  }, [selfIdQuestionResponses.data]);

  const selfIdQuestionsFieldSet = useSelfIdQuestionAnswersFieldSet({
    jobSections,
    existingJobApp,
    selfIdQuestions,
    isGovtContractor: isGovtContractorResponse?.data ?? false,
  });

  //

  // Rest of sections populated from existing job app
  //

  const addressInfoFieldSet = useAddressInfoFieldSet({
    jobSections,
    existingJobApp,
  });

  const educationHistoryFieldSet = useEducationHistoryFieldSet({
    jobSections,
    existingJobApp,
  });

  const workHistoryFieldSet = useWorkHistoryFieldSet({
    jobSections,
    existingJobApp,
  });

  const customQuestionFieldSet = useCustomQuestionAnswersFieldSet({
    jobSections,
    existingJobApp,
  });

  const documentFieldSet = useDocumentFieldSet({
    jobSections,
    existingJobApp,
    orgFriendlyName: props.orgFriendlyName,
    jobExtension: props.jobExtension,
  });

  //

  return (
    <React.Fragment>
      {schema?.jobSections
        ?.sort((a, b) => {
          // The null-handling won't sort nulls last, but we shouldn't be getting back null orders anyway
          if (isNil(a) || isNil(b)) {
            return 0;
          }
          return (a.order ?? 0) - (b.order ?? 0);
        })
        ?.map((section) => {
          switch (section?.jobSectionType) {
            case JobAppSectionResponseSectionType.PERSONAL_INFO:
              return (
                <SelfApplyPersonalInfoAppSection
                  key={section?.id}
                  section={section}
                  fieldSet={personalInfoFieldSet}
                />
              );
            case JobAppSectionResponseSectionType.ADDRESS_INFORMATION:
              return (
                <SelfApplyAddressInfoAppSection
                  key={section?.id}
                  section={section}
                  fieldSet={addressInfoFieldSet}
                />
              );
            case JobAppSectionResponseSectionType.EDUCATION_HISTORY:
              return (
                <SelfApplyEducationHistoryInfoAppSection
                  key={section?.id}
                  section={section}
                  fieldSet={educationHistoryFieldSet}
                />
              );
            case JobAppSectionResponseSectionType.WORK_HISTORY:
              return (
                <SelfApplyWorkHistoryAppSection
                  key={section?.id}
                  section={section}
                  fieldSet={workHistoryFieldSet}
                />
              );
            case JobAppSectionResponseSectionType.CANDIDATE_DOCUMENTS:
              return (
                <SelfApplyCandidateDocumentsAppSection
                  section={section}
                  key={section?.id}
                  documents={documentFieldSet}
                />
              );
            case JobAppSectionResponseSectionType.CUSTOM_QUESTIONS: {
              return (
                <SelfApplyCustomQuestionsAppSection
                  key={section?.id}
                  section={section}
                  fieldSet={customQuestionFieldSet}
                />
              );
            }
            case JobAppSectionResponseSectionType.SELF_IDENTIFICATION: {
              return (
                <SelfApplySelfIDAppSection
                  key={section?.id}
                  orgFriendlyName={props.orgFriendlyName}
                  section={section}
                  fieldSet={selfIdQuestionsFieldSet}
                  questions={selfIdQuestions}
                />
              );
            }
            default:
          }
        })}

      <div
        className={cn(props.submitButtonGoToBottomOfContainer ? "mt-auto" : "")}
      >
        <Button
          disabled={props.submission.isDisabled}
          loading={props.submission.isLoading}
          onClick={async () => {
            props.submission.onSubmit?.();

            validateAllFieldSets(
              personalInfoFieldSet,
              addressInfoFieldSet,
              educationHistoryFieldSet,
              workHistoryFieldSet,
              customQuestionFieldSet,
              documentFieldSet,
              selfIdQuestionsFieldSet,
              customQuestionFieldSet,
            );

            const documentFields = documentFieldSet.values;

            const formRequest = bundleFieldSetsToFormRequest({
              jobSections,
              existingJobApp,
              isGovtContractor: isGovtContractorResponse?.data ?? false,
              orgFriendlyName: props.orgFriendlyName,
              jobExtension: props.jobExtension,
              personalInfoFields: personalInfoFieldSet.values,
              addressInfoFields: addressInfoFieldSet.values,
              educationHistoryFields: educationHistoryFieldSet.values,
              workHistoryFields: workHistoryFieldSet.values,
              customQuestionAnswersFields: customQuestionFieldSet.values,
              selfIdQuestionAnswersFields: selfIdQuestionsFieldSet.values,
              isFinalStep: !documentFields.length,
            });
            const documentRequests = documentFieldsToRequests({
              fields: documentFields,
              orgFriendlyName: props.orgFriendlyName,
              jobExtension: props.jobExtension,
            });

            validateSelfApplyJobApplication({
              formFields: {
                ...formRequest,
                applicationQuestions: customQuestionFieldSet.values,
              },
              formSchema: CandidateSelfApplyJobApplicationSchema,
              documentFields,
              jobSections,
              onSuccess: () => {
                ModalService.openModal(() => {
                  return (
                    <ConfirmModal
                      title="Submit your application?"
                      body="Once you submit your application, this action cannot be undone."
                      onSubmit={async () => {
                        uploadSelfApplyJobApplication({
                          formRequest,
                          documentRequests,
                          uploadForm: props.submission.onUpload.uploadForm,
                          uploadDocument:
                            props.submission.onUpload.uploadDocument,
                          onSubmit: props.submission.onSubmit,
                          onSuccess: props.submission.onUpload.onSuccess,
                          onError: props.submission.onUpload.onError,
                          onSettled: props.submission.onUpload.onSettled,
                        });
                      }}
                      onCancel={() => {}}
                      submittedJobOnForward={props.submittedJobOnForward}
                      forwardJobData={props.forwardJobData}
                    />
                  );
                });
              },
              onError: props.submission.onValidate.onError,
              onSettled: props.submission.onValidate.onSettled,
            });
          }}
          width="full"
          color="navy"
        >
          Submit
        </Button>
      </div>
    </React.Fragment>
  );
};
